import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import AuthService from "../services/AuthService";
import StorageService from "../services/StorageService";
import AuthPageLogo from "../components/AuthPageLogo";
import FounderAccessHeroPanel from "../components/auth/FounderAccessHeroPanel";
import {
  clearPostLoginRedirect,
  isSafeAppRelativePath,
  resolvePostLoginRedirect,
} from "../utils/authRedirect";
import "../assets/styles/login-page.scss";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, user, message, success } = await AuthService.login(email, password);

      if (token) {
          StorageService.setAuth({ token, user }, { remember, cookieDays: 7 });

        if (success) {
          toast.success(message);
          try {
            const { clearCurrentRole } = await import("../utils/roleSwitcher");
            const accessRoleId = Number(user?.access_role_id);
            if (
              user?.access_account_type === "support_manager" ||
              user?.access_account_type === "support_user" ||
              user?.access_account_type === "logistics_manager" ||
              user?.access_account_type === "logistics_user" ||
              user?.access_role_level === "support_manager" ||
              user?.access_role_level === "support_user" ||
              user?.access_role_level === "logistics_manager" ||
              user?.access_role_level === "logistics_user" ||
              user?.access_role_code === "support-manager" ||
              user?.access_role_code === "support" ||
              user?.access_role_code === "logistics-manager" ||
              user?.access_role_code === "logistics" ||
              (Number.isFinite(accessRoleId) && accessRoleId > 0 &&
                (user?.access_role_level === "support_user" ||
                  user?.access_account_type === "support_user"))
            ) {
              clearCurrentRole();
            }
          } catch {
            // ignore role cleanup failures
          }
          try {
            const { getOnboardingRedirect } = await import("../utils/onboarding/getOnboardingRedirect");
            const onboardingRoute = await getOnboardingRedirect();
            if (onboardingRoute) {
              navigate(onboardingRoute);
              return;
            }
          } catch {
            // fall back to normal dashboard routing
          }

          const redirectTo = resolvePostLoginRedirect(location);
          if (isSafeAppRelativePath(redirectTo)) {
            clearPostLoginRedirect();
            navigate(redirectTo, { replace: true });
            return;
          }

          const { getDashboardRoute } = await import("../utils/getDashboardRoute");
          navigate(getDashboardRoute());
        } else {
          toast.error(message);
        }
      } else {
        toast.error(message);
      }
    } catch (err) {
      const message = err?.response?.data?.message || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page login-page--split">
      <div className="login-page__shell login-page__shell--split">
        <FounderAccessHeroPanel />

        <main className="login-page__form-panel login-page__form-panel--split">
          <div className="login-page__form-inner">
            <div className="login-page__form-logo">
              <AuthPageLogo />
            </div>
            <h2 className="login-page__form-title">Welcome back</h2>
            <p className="login-page__form-subtitle">Sign in to your account</p>

            <form onSubmit={handleLogin} noValidate>
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="login-email">
                  Email or username
                </label>
                <div className="login-page__input-wrap">
                  <FaEnvelope className="login-page__input-icon" aria-hidden />
                  <input
                    id="login-email"
                    type="text"
                    className="login-page__input login-page__input--tinted"
                    placeholder="Enter your email or username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="login-page__field">
                <label className="login-page__label" htmlFor="login-password">
                  Password
                </label>
                <div className="login-page__input-wrap">
                  <FaLock className="login-page__input-icon" aria-hidden />
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    className="login-page__input login-page__input--tinted"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-page__input-toggle"
                    onClick={() => setShowPassword((open) => !open)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="login-page__options">
                <label className="login-page__remember" htmlFor="remember">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
                <Link to="/forgot-password" className="login-page__forgot">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" className="login-page__submit" disabled={loading}>
                <FaLock aria-hidden />
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <div className="login-page__divider">Or continue with</div>

            <button className="login-page__google" type="button">
              <img
                src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                alt=""
                aria-hidden
              />
              Continue with Google
            </button>

            <p className="login-page__signup">
              Don&apos;t have an account? <Link to="/signup">Sign up</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
